import { useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { FormattedAxnAdminUserInvitation } from "@idw-types/data/axn-admin-user-invitation";
import { IDWSession } from "@idw-types/data/idw-session";
import { RelyingPartyRole } from "@idw-types/common";
import { formatDateToString, sortDates } from "@utils/date-util";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { sortStrings } from "@utils/string-util";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { OrganizationInvitationTableCell } from "../organization-invitation-table-cell/organization-invitation-table-cell";
import styles from "./organization-invitations-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type OrganizationInvitationsTableContainerProps = {
    session: IDWSession;
    axnAdminUserInvitations: Array<FormattedAxnAdminUserInvitation>;
    axnRelyingParties: Array<AxnRelyingParty>;
    tableState: TableState;
    isPageHeight: boolean;
};

export type OrganizationInvitationsTableDataRow = FormattedAxnAdminUserInvitation & {
    organizationName: string;
    relyingPartyNames: string;
    roleName: string;
};

export const OrganizationInvitationsTableContainer = (props: OrganizationInvitationsTableContainerProps) => {
    const {
        session,
        axnAdminUserInvitations,
        axnRelyingParties,
        tableState,
        isPageHeight
    } = props;

    const dispatch = useDispatch();

    const organizationInvitationsTableData: Array<OrganizationInvitationsTableDataRow> = useMemo(() => {
        return axnAdminUserInvitations.map(invite => {
            const sessionUserAllowedRelyingParties = extractRelyingPartiesForPropertyName(
                UserPermissionTypes.LIST_USERS,
                session.user.axnAdminUserRPProperties,
                axnRelyingParties
            );

            const sessionUserRpIds = sessionUserAllowedRelyingParties.map(rp => rp.id);

            let relyingPartyString = resources.common.notFound;
            let relyingPartyRoleString = resources.common.notFound;

            if (invite.relyingPartyRoles.length > 0) {
                const allowedRelyingPartyRoles: Array<RelyingPartyRole> = []
                invite.relyingPartyRoles.forEach(rpr => {
                    //if the session user has access to view the select role, add it to columns
                    if (sessionUserRpIds.includes(rpr.relyingParty.id)) {
                        allowedRelyingPartyRoles.push(rpr);
                    }
                });

                if (allowedRelyingPartyRoles.length > 0) {
                    relyingPartyString = allowedRelyingPartyRoles.map((rpr, index) => {
                        if (allowedRelyingPartyRoles.length > 1) {
                            return `(${index + 1}) ${rpr.relyingParty.name}`;
                        } else {
                            return rpr.relyingParty.name;
                        }
                    }).join(", ");
                    relyingPartyRoleString = allowedRelyingPartyRoles.map((rpr, index) => {
                        const bundleName = rpr.bundle ? rpr.bundle.name : "Custom";

                        if (allowedRelyingPartyRoles.length > 1) {
                            return `(${index + 1}) ${bundleName}`;
                        } else {
                            return bundleName;
                        }
                    }).join(", ");
                }
            }

            const roleName = invite.organizationRole?.name || relyingPartyRoleString;

            return {
                ...invite,
                organizationName: invite.organization?.name || resources.common.notFound,
                relyingPartyNames: relyingPartyString,
                roleName: roleName || "--",
                email: invite.email || "--"
            };
        });
    }, [axnAdminUserInvitations]);

    const columnHelper = createColumnHelper<OrganizationInvitationsTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("email", {
            header: resources.tables.organizationInvitations.columns.email.header,
            filterFn: "fuzzyText",
            cell: (props) => <OrganizationInvitationTableCell data={props.row.original} />,
            sortingFn: (a, b) => sortStrings(a.original.email, b.original.email)
        }),
        columnHelper.accessor("organizationName", {
            id: "organizationName",
            header: resources.tables.organizationInvitations.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
        }),
        columnHelper.accessor("relyingPartyNames", {
            id: "relyingPartyNames",
            header: resources.tables.organizationInvitations.columns.relyingParty.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyNames, b.original.relyingPartyNames)
        }),
        columnHelper.accessor("roleName", {
            header: resources.tables.organizationInvitations.columns.role.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("status", {
            header: resources.tables.organizationInvitations.columns.status.header,
            filterFn: "fuzzyText",
            cell: (props) => {
                const value = props.getValue();
                switch (value) {
                    case "active":
                        return <Badge bg={"success"}>{value}</Badge>;
                    case "used":
                        return <Badge bg={"primary"}>{value}</Badge>;
                    case "canceled":
                        return <Badge bg={"danger"}>{value}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            }
        }),
        columnHelper.accessor("createdDate", {
            header: resources.tables.organizationInvitations.columns.created.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            filterFn: "dateTimeFilter",
            sortingFn: (a, b) => sortDates(a.original.createdDate, b.original.createdDate)
        }),
        columnHelper.accessor("inviterName", {
            header: resources.tables.organizationInvitations.columns.inviter.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.inviterName, b.original.inviterName)
        })
    ]), [organizationInvitationsTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: organizationInvitationsTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_INVITATIONS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_INVITATIONS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.ORGANIZATION_INVITATIONS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer>
            <IdwTable
                id="organization-invitations"
                useTableResult={useTableResult}
                className={styles.organizationInvitationsTable}
                headerColumnKeyPrefix={"organization-invitations"}
            />
            <PaginationContainer
                idPrefix="organization-invitations"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};